import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Ticket, MessageSquare, CheckCircle2, Clock, AlertTriangle, Link2, User, Send, RotateCcw, Filter, Search, X, UserPlus, Sparkles, Pencil, Trash2, History, Smile } from 'lucide-react';
import { toast } from 'sonner';
import { ZohoCustomerLink } from '../../components/ZohoLinks';
import MentionTextarea from '../../components/MentionTextarea';
import MentionText from '../../components/MentionText';

function fmtDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }) {
  if (status === 'resolved') return <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-0.5" />Resolved</Badge>;
  if (status === 'reopened') return <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200"><RotateCcw className="w-3 h-3 mr-0.5" />Reopened</Badge>;
  return <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200"><Clock className="w-3 h-3 mr-0.5" />Open</Badge>;
}

/* =============================================================================
 * ActivityTimeline — chronological audit feed for a ticket
 * Parses the comments array + ticket meta into typed events:
 *   - created       (initial ticket creation, from ticket.created_at + assignee)
 *   - comment       (regular comment)
 *   - resolved      ([RESOLVED] prefix in comment)
 *   - reopened      ([REOPENED] prefix in comment)
 *   - mention       (any comment containing @<user>)
 *   - screenshot    (rendered inline on the parent comment, not a separate event)
 *
 * Renders left-rail vertical line + colored circle per event + content card.
 * =============================================================================*/

const TICKET_EVENT_META = {
  created:    { icon: Sparkles,    color: 'text-violet-600',  bg: 'bg-violet-100', ring: 'ring-violet-200', label: 'Ticket created' },
  assigned:   { icon: UserPlus,    color: 'text-blue-600',    bg: 'bg-blue-100',   ring: 'ring-blue-200',   label: 'Assigned' },
  comment:    { icon: MessageSquare, color: 'text-slate-500', bg: 'bg-slate-100',  ring: 'ring-slate-200',  label: 'Comment' },
  deleted:    { icon: Trash2,      color: 'text-rose-500',    bg: 'bg-rose-100',   ring: 'ring-rose-200',   label: 'Comment deleted' },
  resolved:   { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100', ring: 'ring-emerald-200', label: 'Resolved' },
  reopened:   { icon: RotateCcw,   color: 'text-amber-600',   bg: 'bg-amber-100',  ring: 'ring-amber-200',  label: 'Reopened' },
};

function fmtRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function buildTicketEvents(ticket) {
  if (!ticket) return [];
  const events = [];
  // 1. Initial creation
  events.push({
    type: 'created',
    actor: ticket.created_by || 'System',
    at: ticket.created_at,
    detail: { assignee: ticket.assigned_to, rule: ticket.rule, description: ticket.description },
  });
  // 2. Comments + status changes
  for (const c of (ticket.comments || [])) {
    const text = c.text || '';
    if (text.startsWith('[RESOLVED]')) {
      events.push({
        type: 'resolved',
        actor: c.by || ticket.resolved_by || 'System',
        at: c.at,
        detail: { text: text.replace(/^\[RESOLVED\]\s*/, ''), screenshot_url: c.screenshot_url, id: c.id, reactions: c.reactions || [] },
      });
    } else if (text.startsWith('[REOPENED]')) {
      events.push({
        type: 'reopened',
        actor: c.by || 'System',
        at: c.at,
        detail: { text: text.replace(/^\[REOPENED\]\s*/, ''), id: c.id, reactions: c.reactions || [] },
      });
    } else if (c.deleted) {
      events.push({
        type: 'deleted',
        actor: c.deleted_by || c.by || 'System',
        at: c.deleted_at || c.at,
        detail: {
          id: c.id,
          original_at: c.at,
          original_by: c.by,
          deleted_text: c.deleted_text || '',
          revisions: c.revisions || [],
          reactions: c.reactions || [],
        },
      });
    } else {
      events.push({
        type: 'comment',
        actor: c.by || 'System',
        at: c.at,
        detail: {
          text,
          screenshot_url: c.screenshot_url,
          id: c.id,
          by_user_id: c.by_user_id,
          edited: !!c.edited,
          edited_at: c.edited_at,
          edited_by: c.edited_by,
          revisions: c.revisions || [],
          reactions: c.reactions || [],
        },
      });
    }
  }
  // Sort ascending by timestamp; fall back to insertion order
  events.sort((a, b) => {
    const ta = a.at ? new Date(a.at).getTime() : 0;
    const tb = b.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });
  return events;
}

const REACTIONS = ['👍', '❤️', '👀', '✅'];

function CommentReactions({ commentId, reactions, me, onToggle }) {
  const list = reactions || [];
  // Group by emoji
  const groups = REACTIONS.map((e) => {
    const items = list.filter((r) => r.emoji === e);
    const meId = me?.id || me?.user_id || '';
    const meName = me?.full_name || me?.email || '';
    const mine = items.some((r) => (r.user_id && r.user_id === meId) || (!r.user_id && r.user_name === meName));
    return { emoji: e, count: items.length, mine, names: items.map((r) => r.user_name).filter(Boolean) };
  });
  const anyCount = groups.some((g) => g.count > 0);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`comment-reactions-${commentId}`}>
      {groups.filter((g) => g.count > 0).map((g) => (
        <button key={g.emoji} onClick={() => onToggle(commentId, g.emoji)}
          data-testid={`reaction-chip-${commentId}-${g.emoji}`}
          title={g.names.join(', ')}
          className={`text-[11px] leading-none px-1.5 py-0.5 rounded-full border transition inline-flex items-center gap-1 ${
            g.mine
              ? 'bg-violet-50 border-violet-300 text-violet-700 hover:bg-violet-100'
              : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
          }`}>
          <span>{g.emoji}</span>
          <span className="tabular-nums text-[10px]">{g.count}</span>
        </button>
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={`reaction-add-${commentId}`}
            title="Add reaction"
            className={`text-slate-400 hover:text-violet-600 rounded-full ${anyCount ? 'p-0.5' : 'p-1'} hover:bg-slate-100 transition inline-flex items-center`}>
            <Smile className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" side="top" className="w-auto p-1 flex gap-1">
          {REACTIONS.map((e) => {
            const g = groups.find((x) => x.emoji === e);
            return (
              <button key={e} onClick={() => onToggle(commentId, e)}
                data-testid={`reaction-picker-${commentId}-${e}`}
                className={`text-lg leading-none px-1.5 py-1 rounded hover:bg-slate-100 transition ${g.mine ? 'bg-violet-50' : ''}`}>
                {e}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function fmtStamp(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function RevisionsPopover({ revisions, currentText, edited_by, edited_at, testId }) {
  if (!revisions || revisions.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-violet-600 underline decoration-dotted"
          data-testid={testId} title="View edit history">
          <History className="w-2.5 h-2.5" />edited{edited_by ? ` by ${edited_by}` : ''}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <div className="px-3 py-2 border-b bg-slate-50">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Edit history · {revisions.length + 1} version{revisions.length ? 's' : ''}</p>
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 text-[11px]">
          {/* current text at top */}
          <div className="px-3 py-2">
            <p className="flex items-center justify-between text-[9px] uppercase tracking-wide text-emerald-600 font-semibold mb-0.5">
              <span>Current · v{revisions.length + 1}</span>
              <span className="text-slate-400 font-normal normal-case">{edited_by || ''} · {fmtStamp(edited_at)}</span>
            </p>
            <p className="text-slate-700 whitespace-pre-wrap">{currentText || <span className="italic text-slate-300">(empty)</span>}</p>
          </div>
          {revisions.slice().reverse().map((r, i) => {
            const versionNum = revisions.length - i;
            return (
              <div key={i} className="px-3 py-2 bg-slate-50/40">
                <p className="flex items-center justify-between text-[9px] uppercase tracking-wide text-slate-400 font-semibold mb-0.5">
                  <span>v{versionNum}</span>
                  <span className="normal-case font-normal">{r.by || ''} · {fmtStamp(r.at)}</span>
                </p>
                <p className="text-slate-600 whitespace-pre-wrap">{r.text || <span className="italic text-slate-300">(empty)</span>}</p>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TimelineEvent({ event, isLast, me, onEdit, onDelete, onReact }) {
  const meta = TICKET_EVENT_META[event.type] || TICKET_EVENT_META.comment;
  const Icon = meta.icon;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const canModify = (() => {
    if (event.type !== 'comment') return false;
    if (!me) return false;
    if (me.role === 'admin' || me.role === 'manager') return true;
    // Author check by user_id (preferred) or name (legacy fallback)
    const uid = me.id || me.user_id;
    if (uid && event.detail?.by_user_id) return event.detail.by_user_id === uid;
    const uname = me.full_name || me.email || '';
    if (!event.detail?.by_user_id && uname && event.actor === uname) {
      // Legacy comments — allow only within 60 min window
      const ageMin = event.at ? (Date.now() - new Date(event.at).getTime()) / 60000 : 999;
      return ageMin <= 60;
    }
    return false;
  })();

  const startEdit = () => { setDraft(event.detail?.text || ''); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(''); };
  const saveEdit = async () => {
    if (!draft.trim() || !event.detail?.id) return;
    setSaving(true);
    try { await onEdit(event.detail.id, draft.trim()); setEditing(false); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    if (!event.detail?.id) return;
    if (!window.confirm('Delete this comment? The activity timeline will still keep an entry.')) return;
    await onDelete(event.detail.id);
  };

  return (
    <div className="relative flex gap-3" data-testid={`timeline-event-${event.type}`}>
      {/* Vertical connector line */}
      {!isLast && <div className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200" />}

      {/* Icon dot */}
      <div className={`relative z-10 w-8 h-8 rounded-full ${meta.bg} ring-4 ring-white flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-4">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-800">{event.actor}</span>
          <span className="text-[11px] text-slate-500">
            {event.type === 'created' && (
              <>created the ticket{event.detail?.assignee && <> and assigned to <span className="text-slate-700 font-medium">{event.detail.assignee}</span></>}</>
            )}
            {event.type === 'comment' && 'added a comment'}
            {event.type === 'deleted' && <span className="text-rose-600 font-medium">deleted a comment</span>}
            {event.type === 'resolved' && <span className="text-emerald-700 font-medium">resolved the ticket</span>}
            {event.type === 'reopened' && <span className="text-amber-700 font-medium">reopened the ticket</span>}
          </span>
          {event.type === 'comment' && event.detail?.edited && (
            <RevisionsPopover
              revisions={event.detail.revisions}
              currentText={event.detail.text}
              edited_by={event.detail.edited_by}
              edited_at={event.detail.edited_at}
              testId={`comment-edited-chip-${event.detail.id}`}
            />
          )}
          <span className="text-[10px] text-slate-400 ml-auto whitespace-nowrap" title={event.at ? new Date(event.at).toLocaleString() : ''}>
            <Clock className="w-2.5 h-2.5 inline mr-0.5" />{fmtRelative(event.at)}
          </span>
        </div>

        {/* Body card */}
        {event.type === 'created' && event.detail?.description && (
          <div className="mt-1.5 text-xs text-slate-600 bg-violet-50/40 border border-violet-100 rounded-md px-3 py-2 leading-relaxed">
            <MentionText text={event.detail.description} />
          </div>
        )}
        {event.type === 'comment' && !editing && event.detail?.text && (
          <div className="mt-1.5 text-xs leading-relaxed border rounded-md px-3 py-2 bg-white border-slate-200 text-slate-700 group relative" data-testid={`comment-body-${event.detail.id}`}>
            <MentionText text={event.detail.text} />
            {event.detail?.screenshot_url && (
              <a href={event.detail.screenshot_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-blue-600 hover:underline text-[10px]">
                <Link2 className="w-3 h-3" />Screenshot attached
              </a>
            )}
            {canModify && (
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
                <button onClick={startEdit} data-testid={`edit-comment-${event.detail.id}`}
                  className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-600" title="Edit">
                  <Pencil className="w-3 h-3" />
                </button>
                <button onClick={doDelete} data-testid={`delete-comment-${event.detail.id}`}
                  className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-rose-600" title="Delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
            <CommentReactions commentId={event.detail.id} reactions={event.detail.reactions} me={me} onToggle={onReact} />
          </div>
        )}
        {event.type === 'comment' && editing && (
          <div className="mt-1.5 space-y-2">
            <Textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              data-testid={`edit-comment-input-${event.detail.id}`}
              className="text-xs min-h-[64px]" />
            <div className="flex items-center gap-2">
              <Button size="sm" className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700" onClick={saveEdit} disabled={saving || !draft.trim()}
                data-testid={`save-comment-${event.detail.id}`}>{saving ? 'Saving…' : 'Save'}</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={cancelEdit}
                data-testid={`cancel-edit-${event.detail.id}`}>Cancel</Button>
            </div>
          </div>
        )}
        {event.type === 'deleted' && (
          <details className="mt-1.5 text-xs border border-rose-100 bg-rose-50/40 rounded-md px-3 py-2 group" data-testid={`deleted-comment-${event.detail.id}`}>
            <summary className="cursor-pointer text-rose-700 text-[11px] list-none flex items-center gap-1">
              <span className="text-slate-400">Originally by {event.detail?.original_by || 'someone'} · {fmtStamp(event.detail?.original_at)}</span>
              <span className="ml-auto text-slate-400 group-open:hidden">click to reveal ›</span>
              <span className="ml-auto text-slate-400 hidden group-open:inline">hide ‹</span>
            </summary>
            <div className="mt-2 text-slate-500 line-through whitespace-pre-wrap">
              <MentionText text={event.detail?.deleted_text || '(no text)'} />
            </div>
            {event.detail?.revisions?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-rose-200/60">
                <p className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Prior versions ({event.detail.revisions.length})</p>
                {event.detail.revisions.slice().reverse().map((r, i) => (
                  <div key={i} className="text-[11px] text-slate-500 mb-1">
                    <span className="text-slate-400">{r.by || ''} · {fmtStamp(r.at)}: </span>
                    <span className="whitespace-pre-wrap">{r.text}</span>
                  </div>
                ))}
              </div>
            )}
          </details>
        )}
        {(event.type === 'resolved' || event.type === 'reopened') && event.detail?.text && (
          <div className={`mt-1.5 text-xs leading-relaxed border rounded-md px-3 py-2 ${
            event.type === 'resolved' ? 'bg-emerald-50/40 border-emerald-100 text-emerald-900' :
            'bg-amber-50/40 border-amber-100 text-amber-900'
          }`}>
            <MentionText text={event.detail.text} />
            {event.detail?.screenshot_url && (
              <a href={event.detail.screenshot_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-blue-600 hover:underline text-[10px]">
                <Link2 className="w-3 h-3" />Screenshot attached
              </a>
            )}
            <CommentReactions commentId={event.detail.id} reactions={event.detail.reactions} me={me} onToggle={onReact} />
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityTimeline({ ticket, me, onEdit, onDelete, onReact }) {
  const events = buildTicketEvents(ticket);
  const counts = events.reduce((acc, e) => { acc[e.type] = (acc[e.type] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="ticket-activity-timeline">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-violet-500" /> Activity Timeline ({events.length})
        </p>
        <div className="flex items-center gap-1.5 text-[9px] text-slate-400">
          {counts.comment > 0 && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{counts.comment} comment{counts.comment > 1 ? 's' : ''}</span>}
          {counts.deleted > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{counts.deleted} deleted</span>}
          {counts.resolved > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{counts.resolved} resolved</span>}
          {counts.reopened > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{counts.reopened} reopened</span>}
        </div>
      </div>

      <div className="max-h-72 overflow-y-auto pr-1">
        {events.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No activity yet</p>
        ) : (
          events.map((e, i) => (
            <TimelineEvent key={`${e.type}-${e.detail?.id || e.at}-${i}`} event={e} isLast={i === events.length - 1}
              me={me} onEdit={onEdit} onDelete={onDelete} onReact={onReact} />
          ))
        )}
      </div>
    </div>
  );
}

export default function TicketsPage() {
  const { user: me } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentMentions, setCommentMentions] = useState([]);
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [resolveMode, setResolveMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchTickets = useCallback(() => {
    setLoading(true);
    const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
    api.get(`/training/reports/tickets${params}`)
      .then(r => setTickets(r.data.tickets || []))
      .catch(() => toast.error('Failed to load tickets'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Deep-link support: /training/tickets?focus=<ticket_id>
  // Notifications (e.g. "Ticket assigned to you") route users here.
  // We fetch the ticket by id and open the detail dialog directly.
  // If it's a resolved ticket, we also flip statusFilter so the row is visible
  // in the list behind the dialog.
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/training/reports/tickets/${focusId}`);
        if (cancelled) return;
        setSelectedTicket(r.data);
        if (r.data?.status === 'resolved' && statusFilter === 'open') {
          setStatusFilter('all');
        }
      } catch (err) {
        toast.error('Could not open the ticket — it may have been removed or you do not have access.');
      } finally {
        // Clear the query param so refreshing the page doesn't re-open the dialog.
        const next = new URLSearchParams(searchParams);
        next.delete('focus');
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, statusFilter, setSearchParams]);

  const filtered = tickets.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (t.student_name || '').toLowerCase().includes(s) ||
           (t.enrollment_no || '').toLowerCase().includes(s) ||
           (t.assigned_to || '').toLowerCase().includes(s) ||
           (t.description || '').toLowerCase().includes(s);
  });

  const openCount = tickets.filter(t => t.status === 'open' || t.status === 'reopened').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved').length;

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSending(true);
    try {
      await api.post(`/training/reports/tickets/${selectedTicket.id}/comment`, {
        text: commentText,
        screenshot_url: screenshotUrl,
        mentioned_user_ids: commentMentions,
      });
      toast.success(commentMentions.length > 0
        ? `Comment added & ${commentMentions.length} teammate${commentMentions.length > 1 ? 's' : ''} notified`
        : 'Comment added');
      setCommentText('');
      setScreenshotUrl('');
      setCommentMentions([]);
      // Refresh ticket
      const r = await api.get(`/training/reports/tickets/${selectedTicket.id}`);
      setSelectedTicket(r.data);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (!commentText.trim()) { toast.error('Resolution comment is required'); return; }
    setSending(true);
    try {
      await api.post(`/training/reports/tickets/${selectedTicket.id}/resolve`, {
        comment: commentText,
        screenshot_url: screenshotUrl,
        mentioned_user_ids: commentMentions,
      });
      toast.success(commentMentions.length > 0
        ? `Resolved & ${commentMentions.length} teammate${commentMentions.length > 1 ? 's' : ''} notified`
        : 'Ticket resolved');
      setCommentText('');
      setScreenshotUrl('');
      setCommentMentions([]);
      setResolveMode(false);
      const r = await api.get(`/training/reports/tickets/${selectedTicket.id}`);
      setSelectedTicket(r.data);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    } finally {
      setSending(false);
    }
  };

  const handleReopen = async () => {
    try {
      await api.post(`/training/reports/tickets/${selectedTicket.id}/reopen`);
      toast.success('Ticket reopened');
      const r = await api.get(`/training/reports/tickets/${selectedTicket.id}`);
      setSelectedTicket(r.data);
      fetchTickets();
    } catch (err) {
      toast.error('Failed to reopen');
    }
  };

  const handleEditComment = async (commentId, newText) => {
    try {
      await api.patch(`/training/reports/tickets/${selectedTicket.id}/comment/${commentId}`, { text: newText });
      toast.success('Comment updated');
      const r = await api.get(`/training/reports/tickets/${selectedTicket.id}`);
      setSelectedTicket(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update comment');
      throw err;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/training/reports/tickets/${selectedTicket.id}/comment/${commentId}`);
      toast.success('Comment deleted');
      const r = await api.get(`/training/reports/tickets/${selectedTicket.id}`);
      setSelectedTicket(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const handleReactComment = async (commentId, emoji) => {
    // Optimistic: update the local ticket immediately, roll back on failure.
    const meId = me?.id || me?.user_id || '';
    const meName = me?.full_name || me?.email || '';
    setSelectedTicket((prev) => {
      if (!prev) return prev;
      const comments = (prev.comments || []).map((c) => {
        if (c.id !== commentId) return c;
        const list = c.reactions || [];
        const already = list.find((r) => r.emoji === emoji && ((r.user_id && r.user_id === meId) || (!r.user_id && r.user_name === meName)));
        const next = already
          ? list.filter((r) => r !== already)
          : [...list, { emoji, user_id: meId, user_name: meName, at: new Date().toISOString() }];
        return { ...c, reactions: next };
      });
      return { ...prev, comments };
    });
    try {
      await api.post(`/training/reports/tickets/${selectedTicket.id}/comment/${commentId}/reaction`, { emoji });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to react');
      // On failure, refetch the ticket to sync back
      try {
        const r = await api.get(`/training/reports/tickets/${selectedTicket.id}`);
        setSelectedTicket(r.data);
      } catch (_e) { /* ignore */ }
    }
  };

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Anomaly Tickets</h1>
        <p className="text-sm text-slate-400 mt-0.5">Track and resolve student anomaly issues</p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-sm">
          <Clock className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-xs font-medium text-amber-700">{openCount} Open</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-sm">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          <span className="text-xs font-medium text-emerald-700">{resolvedCount} Resolved</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
          <Input placeholder="Search by student, assignee, description..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-xs rounded-sm" data-testid="ticket-search" />
        </div>
        <div className="flex items-center border rounded-sm overflow-hidden" data-testid="ticket-status-filter">
          {[{ v: 'all', l: 'All' }, { v: 'open', l: 'Open' }, { v: 'resolved', l: 'Resolved' }].map(opt => (
            <button
              key={opt.v}
              onClick={() => setStatusFilter(opt.v)}
              className={`px-3 py-1.5 text-[10px] font-medium transition-colors ${statusFilter === opt.v ? 'bg-violet-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              data-testid={`ticket-filter-${opt.v}`}
            >{opt.l}</button>
          ))}
        </div>
      </div>

      {/* Tickets Table */}
      <Card className="border p-0">
        <div className="px-4 py-2.5 border-b bg-slate-50/50 flex items-center gap-2">
          <Ticket className="w-3.5 h-3.5 text-violet-500" />
          <span className="text-[10px] uppercase text-slate-400 font-semibold">Tickets</span>
          <span className="text-[10px] text-slate-400">&middot;</span>
          <span className="text-[10px] text-slate-500">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="h-32 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Ticket className="w-8 h-8 text-slate-200 mx-auto mb-3" />
            <p className="text-xs text-slate-400">No tickets found</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[10px] uppercase">Student</TableHead>
              <TableHead className="text-[10px] uppercase">Enrollment</TableHead>
              <TableHead className="text-[10px] uppercase">Rule</TableHead>
              <TableHead className="text-[10px] uppercase">Assigned To</TableHead>
              <TableHead className="text-[10px] uppercase">Created By</TableHead>
              <TableHead className="text-[10px] uppercase">Created</TableHead>
              <TableHead className="text-[10px] uppercase">Status</TableHead>
              <TableHead className="text-[10px] uppercase">Comments</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(t => (
                <TableRow key={t.id} className="cursor-pointer hover:bg-violet-50/30" onClick={() => setSelectedTicket(t)} data-testid={`ticket-row-${t.id}`}>
                  <TableCell className="text-sm font-medium">{t.student_name}</TableCell>
                  <TableCell><ZohoCustomerLink enrollmentNo={t.enrollment_no} zohoCustomerId="" /></TableCell>
                  <TableCell><Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700"><AlertTriangle className="w-3 h-3 mr-0.5" />{t.rule}</Badge></TableCell>
                  <TableCell className="text-xs"><span className="flex items-center gap-1"><User className="w-3 h-3 text-slate-400" />{t.assigned_to}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{t.created_by}</TableCell>
                  <TableCell className="text-[10px] text-slate-500">{fmtDate(t.created_at)}</TableCell>
                  <TableCell><StatusBadge status={t.status} /></TableCell>
                  <TableCell><Badge variant="outline" className="text-[9px]"><MessageSquare className="w-3 h-3 mr-0.5" />{(t.comments || []).length}</Badge></TableCell>
                  <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                    <Button variant="outline" size="sm" className="h-6 text-[10px] text-violet-600" onClick={() => setSelectedTicket(t)} data-testid={`view-ticket-${t.id}`}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => { setSelectedTicket(null); setResolveMode(false); setCommentText(''); setScreenshotUrl(''); setCommentMentions([]); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-violet-500" />
                  Ticket: {selectedTicket.student_name}
                  <StatusBadge status={selectedTicket.status} />
                </DialogTitle>
              </DialogHeader>

              {/* Ticket Info */}
              <div className="grid grid-cols-2 gap-3 text-xs border rounded-sm p-3 bg-slate-50/50">
                <div><span className="text-slate-400">Enrollment:</span> <span className="font-medium">{selectedTicket.enrollment_no}</span></div>
                <div><span className="text-slate-400">Course:</span> <span>{selectedTicket.course}</span></div>
                <div><span className="text-slate-400">Rule:</span> <span className="text-amber-700 font-medium">{selectedTicket.rule}</span></div>
                <div><span className="text-slate-400">Assigned To:</span> <span className="font-medium">{selectedTicket.assigned_to}</span></div>
                {selectedTicket.resolved_by && (
                  <>
                    <div><span className="text-slate-400">Resolved By:</span> <span className="text-emerald-600 font-medium">{selectedTicket.resolved_by}</span></div>
                    <div><span className="text-slate-400">Resolved At:</span> <span>{fmtDate(selectedTicket.resolved_at)}</span></div>
                  </>
                )}
              </div>

              {/* Activity Timeline */}
              <ActivityTimeline ticket={selectedTicket} me={me} onEdit={handleEditComment} onDelete={handleDeleteComment} onReact={handleReactComment} />

              {/* Add Comment / Resolve */}
              <div className="border-t pt-3 space-y-2">
                {resolveMode ? (
                  <p className="text-[10px] uppercase text-emerald-600 font-semibold">Resolve Ticket</p>
                ) : (
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">
                    Add Comment <span className="text-slate-400 normal-case">(use @ to mention a teammate)</span>
                  </p>
                )}
                <MentionTextarea
                  placeholder={resolveMode ? "Resolution comment (required). Type @ to mention..." : "Write a comment, type @ to mention a teammate..."}
                  value={commentText}
                  onChange={setCommentText}
                  onMentionsChange={setCommentMentions}
                  className="text-xs min-h-[60px]"
                  data-testid="ticket-comment-input"
                />
                {commentMentions.length > 0 && (
                  <div className="text-[10px] text-violet-600 flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
                    {commentMentions.length} teammate{commentMentions.length > 1 ? 's' : ''} will be notified
                  </div>
                )}
                <Input
                  placeholder="Screenshot URL (optional)"
                  value={screenshotUrl}
                  onChange={e => setScreenshotUrl(e.target.value)}
                  className="text-xs h-8"
                  data-testid="ticket-screenshot-input"
                />
                <div className="flex items-center gap-2 justify-end">
                  {(selectedTicket.status === 'open' || selectedTicket.status === 'reopened') && !resolveMode && (
                    <Button variant="outline" size="sm" className="text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => setResolveMode(true)} data-testid="ticket-resolve-mode-btn">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Resolve Ticket
                    </Button>
                  )}
                  {selectedTicket.status === 'resolved' && (
                    <Button variant="outline" size="sm" className="text-xs text-amber-600 border-amber-200 hover:bg-amber-50" onClick={handleReopen} data-testid="ticket-reopen-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Reopen
                    </Button>
                  )}
                  {resolveMode ? (
                    <>
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => setResolveMode(false)}>Cancel</Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={handleResolve} disabled={sending} data-testid="ticket-confirm-resolve">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{sending ? 'Resolving...' : 'Confirm Resolve'}
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-xs" onClick={handleComment} disabled={sending || !commentText.trim()} data-testid="ticket-send-comment">
                      <Send className="w-3.5 h-3.5 mr-1" />{sending ? 'Sending...' : 'Send'}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
